/**
 * Rotate daily content picks deterministically by calendar day.
 */

import type {
  CommunityThread,
  CuratedArticle,
  CuratedVideo,
} from "./gmat-resources";
import {
  COMMUNITY_THREADS,
  CURATED_ARTICLES,
  CURATED_VIDEOS,
  INSTAGRAM_ACCOUNTS,
} from "./gmat-resources";

function dayIndex(date = new Date()): number {
  return Math.floor(date.getTime() / 86_400_000);
}

export function pickDaily<T>(items: readonly T[], date = new Date()): T {
  if (items.length === 0) throw new Error("pickDaily: empty list");
  return items[dayIndex(date) % items.length]!;
}

export function pickDailyMany<T>(items: readonly T[], count: number, date = new Date()): T[] {
  const start = dayIndex(date) % items.length;
  const out: T[] = [];
  for (let i = 0; i < count && i < items.length; i++) {
    out.push(items[(start + i) % items.length]!);
  }
  return out;
}

export interface DailyFeed {
  video: CuratedVideo;
  videos: CuratedVideo[];
  article: CuratedArticle;
  articles: CuratedArticle[];
  community: CommunityThread[];
  tip: { handle: string; text: string; url: string };
}

const DAILY_TIPS = [
  { handle: "@gmatpoint", text: "On DS: if you can answer with statement 1 alone, don't look at statement 2 yet.", url: "https://www.instagram.com/gmatpoint/" },
  { handle: "@gmat_club", text: "Today's QOTD: strengthen the conclusion — eliminate answers that only restate it.", url: "https://www.instagram.com/gmat_club/" },
  { handle: "@magooshgmat", text: "Pacing rule: if you're stuck 90+ seconds, mark and move — come back with fresh eyes.", url: "https://www.instagram.com/magooshgmat/" },
  { handle: "@egmat_gmatprep", text: "RC trap: extreme language (always, never) is rarely the main idea.", url: "https://www.instagram.com/egmat_gmatprep/" },
  { handle: "@theofficialgmat", text: "Focus Edition: know your section order before test day — it affects stamina.", url: "https://www.instagram.com/theofficialgmat/" },
];

export function getDailyFeed(date = new Date()): DailyFeed {
  const tip = pickDaily(DAILY_TIPS, date);
  const ig = pickDaily(INSTAGRAM_ACCOUNTS, date);

  return {
    video: pickDaily(CURATED_VIDEOS, date),
    videos: pickDailyMany(CURATED_VIDEOS, 2, date),
    article: pickDaily(CURATED_ARTICLES, date),
    articles: pickDailyMany(CURATED_ARTICLES, 2, date),
    community: pickDailyMany(COMMUNITY_THREADS, 3, date),
    tip: tip ?? {
      handle: ig.handle,
      text: ig.description,
      url: ig.url,
    },
  };
}
